import { DateTime } from "luxon";
import type { ScheduleChangesWindow } from "./types";

/**
 * The longest window a request asks for. The server answers at most 14 days (`MAX_SCHEDULE_CHANGES_WINDOW` in
 * `backend/services/schedule_changes.py`, measured in elapsed hours); a 14-day span in the department's local time is
 * 337 hours across a DST fall-back and would be rejected, so the client stays a day inside it.
 */
export const SERVER_MAX_WINDOW = { days: 13 } as const;

/** A window as the strings a query key holds. `untilIso: null` = "up to now", kept out of the key on purpose. */
export type WindowIsoBounds = { sinceIso: string; untilIso: string | null };

/**
 * A window as a query needs it:
 * - `none`: there is no window (the caller isn't asking yet) — the query stays idle.
 * - `invalid`: a window whose start or end is not a valid instant — the query runs and fails with `error`, so the
 *   surface shows its error state instead of a skeleton that never resolves.
 * - `valid`: `sinceIso` for the cache key and the request, and `untilIso` — null for "up to now", which stays out of
 *   the key so a refetch reaches the present without becoming a new cache entry.
 */
export type WindowBounds = { status: "none" } | { status: "invalid"; error: Error } | { status: "valid"; bounds: WindowIsoBounds };

export const resolveWindowBounds = (changesWindow: ScheduleChangesWindow | null): WindowBounds => {
	if (changesWindow === null) return { status: "none" };
	const sinceIso = changesWindow.since.toISO();
	if (sinceIso === null) {
		return { status: "invalid", error: new Error(`schedule changes: invalid window start (${changesWindow.since.invalidExplanation})`) };
	}
	if (changesWindow.until === null) return { status: "valid", bounds: { sinceIso, untilIso: null } };
	const untilIso = changesWindow.until.toISO();
	if (untilIso === null) {
		return { status: "invalid", error: new Error(`schedule changes: invalid window end (${changesWindow.until.invalidExplanation})`) };
	}
	return { status: "valid", bounds: { sinceIso, untilIso } };
};

/** The cache-key segments of a window: stable strings, or nulls when there is no valid window. */
export const windowKeyParts = (bounds: WindowBounds): [string | null, string | null] =>
	bounds.status === "valid" ? [bounds.bounds.sinceIso, bounds.bounds.untilIso] : [null, null];

/**
 * Where a window ending at `until` can start at the earliest: `since`, or — for a window longer than the server answers
 * — the first start of a day (in the window's zone) at or after `until` minus `SERVER_MAX_WINDOW`. Rounding up to a
 * day keeps the trimmed start the same for every request sent that day, so a poll that brings back the same changes
 * caches the same payload (no rebuilt lookups, no re-rendered grids) instead of a start that slid by five minutes. The
 * captions read this too, so "since …" names the start the data covers.
 */
export const trimToServerWindow = (since: DateTime, until: DateTime): DateTime => {
	const earliest = until.minus(SERVER_MAX_WINDOW).setZone(since.zone);
	if (since >= earliest) return since;
	const dayStart = earliest.startOf("day");
	return dayStart < earliest ? dayStart.plus({ days: 1 }) : dayStart;
};

const isoOrThrow = (value: DateTime, field: string): string => {
	const iso = value.toISO();
	if (iso === null) throw new Error(`schedule changes: invalid ${field} (${value.invalidExplanation})`);
	return iso;
};

/**
 * The `since`/`until` a request sends. An open-ended window ends at the moment the request leaves. A window longer than
 * the server answers is trimmed to its most recent `SERVER_MAX_WINDOW` — a tab left open for weeks keeps working
 * (showing the latest 13 days) instead of failing every poll with a 400.
 */
export const requestBounds = (bounds: WindowIsoBounds): { sinceIso: string; untilIso: string } => {
	const until = bounds.untilIso === null ? DateTime.now() : DateTime.fromISO(bounds.untilIso, { setZone: true });
	const since = DateTime.fromISO(bounds.sinceIso, { setZone: true });
	return { sinceIso: isoOrThrow(trimToServerWindow(since, until), "since"), untilIso: isoOrThrow(until, "until") };
};
